import { useViewport } from 'contexts/viewport';
import useDraggablePlayhead from 'hooks/useDraggablePlayhead';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'shell/components/Box';
import IconButton from 'shell/components/IconButton';
import Loading from 'shell/components/Loading';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import Tooltip from 'shell/components/Tooltip';
import { EVIDENCE_STATUSES, formatVideoTime, isValidVideoEvidenceRange } from 'utils/videoGrading';
import { evidenceColor, EvidenceStatusTag } from './EvidenceTags';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const PLAYHEAD_EDGE_SPACE = 12;

const VideoEvidenceMap = ({
    evidence,
    criteria,
    currentTime,
    duration,
    selectedCriterionId,
    onSeekEvidence,
    onSeekTime,
}) => {
    const { t } = useTranslation('assignment');
    const { device } = useViewport();
    const [zoom, setZoom] = useState(1);
    const timelineRef = useRef(null);
    const playheadContainerRef = useRef(null);
    const playheadLabelRef = useRef(null);
    const [timelineMetrics, setTimelineMetrics] = useState({ scrollLeft: 0, scrollWidth: 0 });
    const [playheadMetrics, setPlayheadMetrics] = useState({ containerWidth: 0, labelWidth: 0 });
    const hasDuration = Number.isFinite(duration) && duration > 0;
    const rows = useMemo(
        () =>
            criteria.map((criterion) => ({
                ...criterion,
                evidence: evidence.filter(
                    (item) =>
                        item.criterionId === criterion.id &&
                        isValidVideoEvidenceRange(item) &&
                        (!hasDuration || item.start < duration)
                ),
            })),
        [criteria, duration, evidence, hasDuration]
    );
    const tickCount = (device === 'mobile' ? 3 : 6) * zoom;
    const ticks = Array.from({ length: tickCount + 1 }, (_, index) => (duration * index) / tickCount);
    const hasTimedEvidence = rows.some((row) =>
        row.evidence.some((item) => !hasDuration || Math.min(item.end, duration) > item.start)
    );
    const playheadTime = hasDuration && Number.isFinite(currentTime) ? clamp(currentTime, 0, duration) : 0;
    const playheadLabel = formatVideoTime(playheadTime);
    const progress = hasDuration ? playheadTime / duration : 0;
    const playheadHandleTransform =
        progress <= 0 ? 'translateX(0)' : progress >= 1 ? 'translateX(-100%)' : 'translateX(-50%)';
    const playheadLinePosition =
        progress <= 0 ? { left: 0 } : progress >= 1 ? { right: 0 } : { left: '50%', transform: 'translateX(-50%)' };
    const measureTimeline = useCallback(() => {
        const timeline = timelineRef.current;
        if (!timeline) return;

        const nextMetrics = {
            scrollLeft: timeline.scrollLeft,
            scrollWidth: timeline.scrollWidth,
        };

        setTimelineMetrics((currentMetrics) =>
            currentMetrics.scrollLeft === nextMetrics.scrollLeft &&
            currentMetrics.scrollWidth === nextMetrics.scrollWidth
                ? currentMetrics
                : nextMetrics
        );
    }, []);

    const playheadPosition = progress * timelineMetrics.scrollWidth - timelineMetrics.scrollLeft;
    const minimumPlayheadLabelPosition = Math.max(0, playheadMetrics.labelWidth / 2 - PLAYHEAD_EDGE_SPACE);
    const maximumPlayheadLabelPosition = Math.max(
        minimumPlayheadLabelPosition,
        playheadMetrics.containerWidth + PLAYHEAD_EDGE_SPACE - playheadMetrics.labelWidth / 2
    );
    const playheadLabelPosition =
        playheadMetrics.containerWidth && playheadMetrics.labelWidth
            ? clamp(playheadPosition, minimumPlayheadLabelPosition, maximumPlayheadLabelPosition)
            : playheadPosition;

    const { dragHandleProps, handleKeyDown } = useDraggablePlayhead({
        currentTime: playheadTime,
        duration,
        onSeekTime,
        timelineRef,
    });

    const seekFromEvidenceClick = (event, item) => {
        let requestedTime = item.start;
        if (event.detail > 0) {
            const bounds = event.currentTarget.getBoundingClientRect();
            const ratio = bounds.width ? clamp((event.clientX - bounds.left) / bounds.width, 0, 1) : 0;
            requestedTime = item.start + ratio * (item.end - item.start);
        }
        onSeekEvidence(item, requestedTime);
    };

    useLayoutEffect(() => {
        measureTimeline();
    }, [hasDuration, hasTimedEvidence, measureTimeline, zoom]);

    const measurePlayhead = useCallback(() => {
        const container = playheadContainerRef.current;
        const label = playheadLabelRef.current;
        const timeline = timelineRef.current;
        if (!container || !label || !timeline) return;

        const nextMetrics = {
            containerWidth: timeline.clientWidth,
            labelWidth: label.getBoundingClientRect().width,
        };
        setPlayheadMetrics((currentMetrics) =>
            currentMetrics.containerWidth === nextMetrics.containerWidth &&
            currentMetrics.labelWidth === nextMetrics.labelWidth
                ? currentMetrics
                : nextMetrics
        );
    }, []);

    useLayoutEffect(() => {
        measurePlayhead();
        const container = playheadContainerRef.current;
        if (!container) return;

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(measurePlayhead);
            observer.observe(container);
            if (timelineRef.current) observer.observe(timelineRef.current);
            if (playheadLabelRef.current) observer.observe(playheadLabelRef.current);
            return () => observer.disconnect();
        }

        window.addEventListener('resize', measurePlayhead);
        return () => window.removeEventListener('resize', measurePlayhead);
    }, [measurePlayhead, playheadLabel]);

    useLayoutEffect(() => {
        const timeline = timelineRef.current;
        if (!timeline) return;

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(measureTimeline);
            observer.observe(timeline);

            return () => observer.disconnect();
        }

        window.addEventListener('resize', measureTimeline);

        return () => {
            window.removeEventListener('resize', measureTimeline);
        };
    }, [hasDuration, hasTimedEvidence, measureTimeline]);

    return (
        <Stack
            spacing={3}
            sx={(theme) => ({
                p: 4,
                borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                background: theme.palette.ensightful.shades[0],
            })}
        >
            <Stack direction={'row'} alignItems={'center'} useFlexGap flexWrap={'wrap'} spacing={5}>
                <Text component={'h2'} variant={'p2'} color={'light'}>
                    {t('video-grading.evidence-map', 'Evidence Map')}
                </Text>
                <Stack
                    direction={'row'}
                    useFlexGap
                    flexWrap={'wrap'}
                    sx={(theme) => ({ borderRadius: '40px', background: theme.palette.ensightful.neutral[100] })}
                >
                    {EVIDENCE_STATUSES.map((status) => (
                        <EvidenceStatusTag key={status} status={status} legend />
                    ))}
                </Stack>
                <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ ml: 'auto' }}>
                    <IconButton
                        kind={'subtract'}
                        size={'small'}
                        variant={'square'}
                        disabled={zoom === 1}
                        aria-label={t('video-grading.zoom-out', 'Zoom out')}
                        onClick={() => setZoom((value) => Math.max(1, value / 2))}
                    />
                    <Text variant={'p3'}>{t('video-grading.zoom', '{{zoom}}× in view', { zoom })}</Text>
                    <IconButton
                        kind={'add'}
                        size={'small'}
                        variant={'square'}
                        disabled={zoom === 8}
                        aria-label={t('video-grading.zoom-in', 'Zoom in')}
                        onClick={() => setZoom((value) => Math.min(8, value * 2))}
                    />
                </Stack>
            </Stack>
            {duration === null ? (
                <Loading sx={{ minHeight: 72 }} />
            ) : hasDuration && hasTimedEvidence ? (
                <Stack direction={'row'} spacing={2}>
                    <Stack sx={{ width: 100, flexShrink: 0, pt: 4 }}>
                        {rows.map((row) => (
                            <Tooltip key={row.id} title={row.title} placement={'right'}>
                                <Text
                                    variant={'p3'}
                                    noWrap
                                    sx={{ height: 28, lineHeight: '28px' }}
                                    color={row.id === selectedCriterionId ? 'primary' : 'default'}
                                >
                                    {row.title}
                                </Text>
                            </Tooltip>
                        ))}
                    </Stack>
                    <Box
                        ref={playheadContainerRef}
                        sx={{
                            position: 'relative',
                            minWidth: 0,
                            flex: 1,
                            overflow: 'visible',
                        }}
                    >
                        <Box
                            ref={timelineRef}
                            sx={{ overflowX: 'auto' }}
                            tabIndex={0}
                            aria-label={t('video-grading.timeline', 'Video evidence timeline')}
                            onScroll={measureTimeline}
                        >
                            <Box sx={{ width: `${zoom * 100}%`, minWidth: '100%', pb: 2 }}>
                                <Box sx={{ position: 'relative', pt: 4 }}>
                                    <Box
                                        aria-hidden={'true'}
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            overflow: 'clip',
                                            zIndex: 1,
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        <Box
                                            aria-hidden={'true'}
                                            {...dragHandleProps}
                                            sx={(theme) => ({
                                                position: 'absolute',
                                                left: `${progress * 100}%`,
                                                top: 0,
                                                bottom: 0,
                                                width: 8,
                                                transform: playheadHandleTransform,
                                                cursor: 'ew-resize',
                                                touchAction: 'none',
                                                pointerEvents: 'auto',
                                                padding: 0,
                                                border: 0,
                                                background: 'transparent',
                                            })}
                                        >
                                            <Box
                                                aria-hidden={'true'}
                                                sx={(theme) => ({
                                                    position: 'absolute',
                                                    top: 0,
                                                    bottom: 0,
                                                    width: '1px',
                                                    background: theme.palette.ensightful.neutral[700],
                                                    ...playheadLinePosition,
                                                })}
                                            />
                                        </Box>
                                    </Box>
                                    {rows.map((row) => (
                                        <Box key={row.id} sx={{ height: 28, display: 'flex', alignItems: 'center' }}>
                                            <Box
                                                sx={(theme) => ({
                                                    height: 16,
                                                    width: '100%',
                                                    borderRadius: '40px',
                                                    background: theme.palette.ensightful.neutral[100],
                                                    position: 'relative',
                                                })}
                                            >
                                                {row.evidence.map((item) => {
                                                    const label = t(
                                                        'video-grading.timeline-evidence',
                                                        '{{criterion}}: {{status}}, {{start}}–{{end}}',
                                                        {
                                                            criterion: row.title,
                                                            status: t(`video-grading.${item.status}`, item.status),
                                                            start: formatVideoTime(item.start),
                                                            end: formatVideoTime(item.end),
                                                        }
                                                    );
                                                    return (
                                                        <Box
                                                            key={item.id}
                                                            component={'button'}
                                                            type={'button'}
                                                            aria-label={label}
                                                            onClick={(event) => seekFromEvidenceClick(event, item)}
                                                            sx={(theme) => ({
                                                                position: 'absolute',
                                                                left: `${(item.start / duration) * 100}%`,
                                                                width: `${((Math.min(item.end, duration) - item.start) / duration) * 100}%`,
                                                                minWidth: 4,
                                                                height: 16,
                                                                padding: 0,
                                                                border: 0,
                                                                borderRadius: '40px',
                                                                cursor: 'pointer',
                                                                background: evidenceColor(item.status),
                                                                '&:focus-visible': {
                                                                    outline: `2px solid ${theme.palette.ensightful.primary[500]}`,
                                                                    outlineOffset: '3px',
                                                                },
                                                                '&:hover': {
                                                                    boxShadow: `0 0 0 2px ${theme.palette.ensightful.primary[200]}`,
                                                                },
                                                            })}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>
                                <Stack direction={'row'} justifyContent={'space-between'} sx={{ mt: 1 }}>
                                    {ticks.map((tick, index) => (
                                        <Text key={index} variant={'p3'} color={'light'}>
                                            {formatVideoTime(tick)}
                                        </Text>
                                    ))}
                                </Stack>
                            </Box>
                        </Box>
                        <Text
                            ref={playheadLabelRef}
                            component={'button'}
                            type={'button'}
                            variant={'p3'}
                            role={'slider'}
                            tabIndex={0}
                            aria-label={t('video-grading.playhead', 'Video playhead at {{time}}', {
                                time: playheadLabel,
                            })}
                            aria-valuemin={0}
                            aria-valuemax={duration}
                            aria-valuenow={playheadTime}
                            aria-valuetext={playheadLabel}
                            {...dragHandleProps}
                            onKeyDown={handleKeyDown}
                            sx={(theme) => ({
                                position: 'absolute',
                                top: 0,
                                left: playheadLabelPosition,
                                zIndex: 2,
                                p: '2px 4px',
                                borderRadius: '8px',
                                whiteSpace: 'nowrap',
                                width: 'max-content',
                                maxWidth: 'none',
                                transform: 'translateX(-50%)',
                                border: 0,
                                cursor: 'ew-resize',
                                touchAction: 'none',
                                background: theme.palette.ensightful.neutral[700],
                                color: theme.palette.ensightful.shades[0],
                                '&:focus-visible': {
                                    outline: `2px solid ${theme.palette.ensightful.primary[500]}`,
                                    outlineOffset: '2px',
                                },
                            })}
                        >
                            {playheadLabel}
                        </Text>
                    </Box>
                </Stack>
            ) : (
                <Text variant={'p3'} color={'light'}>
                    {t('video-grading.no-timed-evidence', 'No timestamped evidence is available for this video.')}
                </Text>
            )}
        </Stack>
    );
};

export default VideoEvidenceMap;
